/**
 * Object storage contract. The API is the only writer/reader of the bucket:
 * uploads and downloads are proxied by the Worker, so no bucket credential,
 * CORS rule or signed URL ever reaches the browser.
 * See docs/adr/0001-worker-mediated-storage.md.
 */

export const STORAGE_PREFIXES = {
  invoice: "invoices",
  branding: "branding",
} as const;

export type StoragePrefix = (typeof STORAGE_PREFIXES)[keyof typeof STORAGE_PREFIXES];

export const STORAGE_KEY_SEPARATOR = "/" as const;

export const STORAGE_MIME_ALLOWLIST: readonly string[] = [
  "image/png",
  "image/jpeg",
  "image/webp",
];

export const STORAGE_EXTENSIONS: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export const STORAGE_CACHE_SECONDS = 300;

export const STORAGE_DISPOSITION = {
  inline: "inline",
  attachment: "attachment",
} as const;

export const STORAGE_OBJECTS_SEGMENT = "objects" as const;

export const STORAGE_UPLOAD_FIELD = "file" as const;

export interface InvoiceAssetKeyInput {
  workspaceId: string;
  invoiceId: string;
  pageSize: string;
  format: string;
}

export const buildInvoiceAssetKey = ({
  workspaceId,
  invoiceId,
  pageSize,
  format,
}: InvoiceAssetKeyInput): string =>
  [
    STORAGE_PREFIXES.invoice,
    workspaceId,
    invoiceId,
    `${pageSize}.${format}`,
  ].join(STORAGE_KEY_SEPARATOR);

export const buildBrandingAssetKey = (workspaceId: string, filename: string): string =>
  [STORAGE_PREFIXES.branding, workspaceId, filename].join(STORAGE_KEY_SEPARATOR);

export const workspaceStoragePrefix = (prefix: StoragePrefix, workspaceId: string): string =>
  `${prefix}${STORAGE_KEY_SEPARATOR}${workspaceId}${STORAGE_KEY_SEPARATOR}`;

export const isWorkspaceScopedKey = (key: string, workspaceId: string): boolean => {
  if (key.includes("..")) return false;
  return Object.values(STORAGE_PREFIXES).some(
    (prefix) => key === `${prefix}${STORAGE_KEY_SEPARATOR}${workspaceId}` || key.startsWith(workspaceStoragePrefix(prefix, workspaceId)),
  );
};

export const isAllowedMime = (mime: string): boolean =>
  STORAGE_MIME_ALLOWLIST.includes(mime.toLowerCase().split(";")[0]?.trim() ?? "");
